import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { AuthShell } from "./AuthShell";
import { GoogleButton } from "./GoogleButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { t } = useLang();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) navigate(user.role === "admin" ? "/admin" : "/dashboard", { replace: true }); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      toast.success("Welcome back!");
      navigate(data.user.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title={t("login_title")} subtitle="Sign in to your INVIXA account">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <Label className="text-slate-300">{t("email")}</Label>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email" className="mt-1.5 bg-white/5 border-white/10" placeholder="you@example.com" />
        </div>
        <div>
          <div className="flex justify-between items-center">
            <Label className="text-slate-300">{t("password")}</Label>
            <Link to="/forgot-password" className="text-xs text-brand-cyan hover:underline" data-testid="forgot-link">{t("forgot_password")}</Link>
          </div>
          <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="login-password" className="mt-1.5 bg-white/5 border-white/10" placeholder="••••••••" />
        </div>
        <Button type="submit" disabled={loading} data-testid="login-submit"
          className="w-full gradient-brand text-brand-navy font-semibold hover:opacity-90">
          {loading ? t("loading") : t("nav_login")}
        </Button>
      </form>
      <div className="my-5 flex items-center gap-3 text-xs text-slate-600"><div className="h-px flex-1 bg-white/10" />OR<div className="h-px flex-1 bg-white/10" /></div>
      <GoogleButton label={t("login_google")} />
      <p className="mt-6 text-center text-sm text-slate-400">
        {t("no_account")} <Link to="/signup" className="text-brand-cyan hover:underline font-medium" data-testid="to-signup">{t("nav_signup")}</Link>
      </p>
    </AuthShell>
  );
}
